import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { createRef, useMemo } from 'react';
import { ITEM_PREFIXES } from '../../constants';
import { Separator } from '../../helperComponents';
import { getFooterItemId } from '../../helpers';
import { useNewListContext, useSelectionContext } from '../Lists/contexts';
import { AccordionItem } from './AccordionItem';
import { BaseItem } from './BaseItem';
import { GroupSelectItem } from './GroupSelectItem';
import { NextListItem } from './NextListItem';
import { isAccordionItem, isGroupItem, isGroupSelectItem, isNextListItem } from './utils';
function getRenderItems({ focusCloseChildIds, focusFlattenItems, flattenItems, isSelectionMultiple, }) {
    if (!focusCloseChildIds) {
        return [null];
    }
    return focusCloseChildIds.flatMap(id => {
        const { itemRef, key, originalId, items } = focusFlattenItems[id];
        const flattenItem = flattenItems[originalId];
        if (isGroupItem(flattenItem) ||
            (!isSelectionMultiple && isGroupSelectItem(flattenItem))) {
            const innerItemsJSX = getRenderItems({
                focusCloseChildIds: items,
                focusFlattenItems,
                flattenItems,
                isSelectionMultiple,
            });
            return [
                _jsx(Separator, { label: flattenItem.label, truncate: flattenItem.truncate, divider: flattenItem.divider, mode: flattenItem.mode }, key + '_separator'),
                ...innerItemsJSX,
            ];
        }
        if (isGroupSelectItem(flattenItem)) {
            return _createElement(GroupSelectItem, Object.assign({}, flattenItem, { items: items, itemRef: itemRef, key: key }));
        }
        if (isAccordionItem(flattenItem)) {
            return _createElement(AccordionItem, Object.assign({}, flattenItem, { items: items, itemRef: itemRef, key: key }));
        }
        if (isNextListItem(flattenItem)) {
            return _createElement(NextListItem, Object.assign({}, flattenItem, { focusId: id, items: items, itemRef: itemRef, key: key }));
        }
        return _createElement(BaseItem, Object.assign({}, flattenItem, { itemRef: itemRef, key: key }));
    });
}
export function useRenderItems(focusCloseChildIds) {
    const { focusFlattenItems, flattenItems } = useNewListContext();
    const { isSelectionMultiple } = useSelectionContext();
    return useMemo(() => getRenderItems({ flattenItems, focusFlattenItems, focusCloseChildIds, isSelectionMultiple }), [flattenItems, focusCloseChildIds, focusFlattenItems, isSelectionMultiple]);
}
export function useCreateBaseItems({ footerActiveElementsRefs }) {
    return useMemo(() => {
        var _a;
        return ({
            searchItem: {
                itemRef: createRef(),
                id: ITEM_PREFIXES.search,
                parentId: ITEM_PREFIXES.default,
                items: [],
                allChildIds: [],
            },
            footerItems: (_a = footerActiveElementsRefs === null || footerActiveElementsRefs === void 0 ? void 0 : footerActiveElementsRefs.map((itemRef, idx) => ({
                id: getFooterItemId(idx),
                itemRef,
                parentId: ITEM_PREFIXES.default,
                items: [],
                allChildIds: [],
            }))) !== null && _a !== void 0 ? _a : [],
        });
    }, [footerActiveElementsRefs]);
}
export function useGroupItemSelection({ id, allChildIds }) {
    const { value, setValue, isSelectionMultiple } = useSelectionContext();
    const { flattenItems } = useNewListContext();
    const baseChildIds = useMemo(() => allChildIds.filter(itemId => {
        const item = flattenItems[itemId];
        return item && !('type' in item);
    }), [allChildIds, flattenItems]);
    const enableChildIds = useMemo(() => baseChildIds.filter(itemId => {
        const item = flattenItems[itemId];
        return item && !('type' in item) && !item.disabled;
    }), [baseChildIds, flattenItems]);
    const checked = isSelectionMultiple
        ? value && Boolean(value.length) && baseChildIds.every(childId => value === null || value === void 0 ? void 0 : value.includes(childId))
        : undefined;
    const allEnabledChecked = isSelectionMultiple
        ? value && Boolean(value.length) && enableChildIds.every(childId => value === null || value === void 0 ? void 0 : value.includes(childId))
        : undefined;
    const indeterminate = isSelectionMultiple
        ? !checked && baseChildIds.some(childId => value === null || value === void 0 ? void 0 : value.includes(childId))
        : baseChildIds.includes(value !== null && value !== void 0 ? value : '');
    const handleOnSelect = () => {
        if (checked || allEnabledChecked) {
            setValue === null || setValue === void 0 ? void 0 : setValue((value) => (value !== null && value !== void 0 ? value : []).filter(itemId => itemId !== id && !enableChildIds.includes(itemId)));
            return;
        }
        setValue === null || setValue === void 0 ? void 0 : setValue((value) => Array.from(new Set([...(value !== null && value !== void 0 ? value : []), ...enableChildIds])));
    };
    return { checked, indeterminate, handleOnSelect };
}
